// =============================================================================
// CRAFT METHODS — the craft/HOW-to-write POND (Pillar 3, cross-user, research-tuned).
//
// The FIRST verified-source lake for the WRITING CRAFT. Where linter_rules holds the
// gate's regex rules, craft_methods holds the composed craft KNOWLEDGE the generators
// and the gate compile from — ONE table with a `craft_class` discriminator over three
// divisions (the two shapes are near-identical rows; the discriminator keeps them one
// pond, the linter_rules precedent):
//
//   'anti_tell_lexicon'  the AI-tell + undersell/flattery tells (a regex source + flags
//                        + reason + optional suggestion) — MIGRATED 1:1 from
//                        AuLinterService.AI_TELL_PATTERNS + AntiSellShortService.
//                        SELL_SHORT_PATTERNS. This division is the SINGLE SOURCE the
//                        render-blocking LintGate reads AND the pre-gen 'avoid these'
//                        block compiles from, so prompt and gate never drift.
//   'storytelling'       the composable narrative framings (a framing template + order)
//                        — MIGRATED 1:1 from arcs/StoryArcService.STORY_ARCS. RAG/soft:
//                        injected into a generating prompt, never a gate.
//   'human_voice'        the POSITIVE reward markers of authentic human voice (concrete
//                        particulars, burstiness, a stated position, contractions,
//                        first-person, active voice, human texture) — sourced from
//                        docs/plans/research-what-lands-2026-06-19.md. A SOFT,
//                        NON-BLOCKING warn/reward, rewarded against Jason's own idiolect
//                        (AntiSellShortService already encodes his undersell-by-default),
//                        NEVER a population mean, NEVER a blocking KILL.
//
// Craft is DATA, not a literal in the algorithm: research-updated over time and applied
// to every user. The pure logic (collectAiTells / lintSellShort / composeArcs) stays in
// the services and takes the compiled craft INJECTED.
//
// A POND behind CraftMethodSource (apps/api/services/lake/CraftMethodService.ts):
// Postgres today, swappable. Cross-user by construction (no userId), mirroring the
// linter_rules / platform_algorithm / industry_vocab lakes.
//
// GOLDEN RISK (mirrors linter_rules): the anti_tell rules are LIVE RegExp — store the
// `pattern` SOURCE + `flags` separately and recompile `new RegExp(source, flags)`; load
// ORDER BY sort_order so gate output stays byte-for-byte. The progression guard asserts
// the pond-compiled craft reproduces the LintGate output byte-for-byte over a fixture.
//
// ADDITIVE ONLY (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool, mirrored here
// in Drizzle and exported from index.ts. A re-seed UPSERTs on (craft_class, craft_key).
// =============================================================================

import { index, integer, pgTable, text, uniqueIndex, varchar } from 'drizzle-orm/pg-core';
import { id, lakeProvenance, timestamps } from './_shared';

export const craftMethods = pgTable(
  'craft_methods',
  {
    id: id(),
    /** Which division this method belongs to — the discriminator over the pond.
     *  'anti_tell_lexicon' | 'storytelling' | 'human_voice'. */
    craftClass: varchar('craft_class', { length: 24 }).notNull(),
    /** Stable per-class key for upsert (slug; unique within craft_class). */
    craftKey: varchar('craft_key', { length: 120 }).notNull(),
    /** Sub-kind WITHIN a class so one division can carry more than one row shape:
     *  anti_tell_lexicon → 'ai_tell' | 'sell_short' (maps to the LintGate rule class);
     *  storytelling → 'arc'; human_voice → 'reward_marker'. */
    kind: varchar('kind', { length: 24 }).notNull(),

    // ── anti_tell_lexicon columns (the regex tells; null for the other divisions) ──
    /** The RegExp SOURCE (e.g. '\\bdelve\\b'). Recompiled with `flags` at load. */
    pattern: text('pattern'),
    /** RegExp flags ('gi' | 'g' | 'gm'). */
    flags: varchar('flags', { length: 8 }),
    /** The reason slug emitted on a match (e.g. 'cliche:cutting_edge', 'hedge'). */
    reason: varchar('reason', { length: 64 }),

    // ── shared display / guidance columns ──
    /** Short display name (storytelling arc name; human_voice marker name). */
    name: varchar('name', { length: 120 }),
    /** One-line premise/summary of the method (storytelling premise; marker summary). */
    premise: text('premise'),
    /** The composed guidance text:
     *  storytelling → the arc FRAMING template (may carry a `${spine}`/`${targetRole}`
     *    placeholder the Source interpolates against ArcContext);
     *  human_voice → the reward-marker guidance surfaced to prompt + soft warn;
     *  sell_short → the user-facing stronger-framing suggestion (mirrors linter_rules). */
    guidance: text('guidance'),

    /** Optional grouping ('cliche'|'overused_word'|'punctuation'|'flattery'|'structure'|
     *  'opener'|'concrete'|'burstiness'|'stance'|… ). */
    category: varchar('category', { length: 24 }),
    /** Where it fires — 'universal' (every English variant; house style) is the default.
     *  Locale-specific spelling lives in its OWN pond, never here. */
    scope: varchar('scope', { length: 16 }).notNull().default('universal'),
    /** Why this method exists / evidence (e.g. 'PNAS 2025 Reinhart'; the what-lands pass). */
    evidence: text('evidence'),
    /** 'high' | 'medium' | 'low' — evidence strength. */
    confidence: varchar('confidence', { length: 8 }),
    /** Match/emit/compose order WITHIN a class — preserved so gate + prompt output stays
     *  byte-for-byte with the in-code arrays/records. */
    sortOrder: integer('sort_order').notNull().default(0),
    ...lakeProvenance,
    ...timestamps,
  },
  (t) => ({
    classIdx: index('craft_methods_class_idx').on(t.craftClass, t.sortOrder),
    // UNIQUE (craft_class, craft_key) — one method per key per class, so a re-seed upserts
    // in place. Apply via the Neon tool:
    //   CREATE UNIQUE INDEX IF NOT EXISTS craft_methods_key_uq
    //     ON craft_methods (craft_class, craft_key);
    keyUq: uniqueIndex('craft_methods_key_uq').on(t.craftClass, t.craftKey),
  }),
);
